import React from 'react';
import { Helmet } from 'react-helmet';

export const SITE_URL = 'https://vidhifarm.in';
const SITE_NAME = 'Vidhi Farm';
const DEFAULT_IMAGE = `${SITE_URL}/farm-og-image.jpg`;

/**
 * Per-page title, description, canonical and social tags.
 *
 * Every page except the blog detail shared one <title> from index.html --
 * "Vidhi Farm - Fresh Organic Produce & Farm Experiences" -- so the farm stay
 * page could not rank for what it is actually about, and Google had one
 * description to show for the whole site.
 *
 * `jsonLd` takes a schema.org object. Search engines use it for rich results:
 * a stay with a price and a location can show both in the result itself.
 */
const Seo = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  jsonLd,
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Organic Farm & Farm Stay in Birbhum, West Bengal`;
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

/** The farm itself — used on the home and contact pages. */
export const farmJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'LodgingBusiness',
  name: SITE_NAME,
  description: 'An organic permaculture farm in Birbhum, West Bengal, offering farm-fresh produce and weekend farm stays.',
  url: SITE_URL,
  image: DEFAULT_IMAGE,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Birbhum',
    addressRegion: 'West Bengal',
    addressCountry: 'IN',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 23.914634, longitude: 87.378244 },
  telephone: '+91 85830 79287',
  priceRange: '₹₹',
  amenityFeature: ['Farm-to-table meals', 'Bonfire evenings', 'Baul music', 'Fruit forest', 'Pond'],
});

/** The weekend package, with its real price so a search result can show it. */
export const stayJsonLd = (pkg) => {
  if (!pkg) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: pkg.name,
    description: pkg.summary,
    image: pkg.heroImage || DEFAULT_IMAGE,
    brand: { '@type': 'Brand', name: SITE_NAME },
    ...(pkg.price != null && {
      offers: {
        '@type': 'Offer',
        price: pkg.price,
        priceCurrency: 'INR',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/farm-stay`,
        // the rate is per person per night, which the unit price makes explicit
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: pkg.price,
          priceCurrency: 'INR',
          unitText: 'per person per night',
        },
      },
    }),
  };
};

export default Seo;
